import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject,
  computed,
  Injector,
  runInInjectionContext,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  addToFavourite,
  ConfigService,
  favourites,
  injectAreas,
  removeFromFavourites,
  ResourceAttendee,
  ResourceService,
} from '@assist/shared/data';
import { NewResourceMapComponent } from '@assist/ui/new-floorplans';
import { translation } from '@cue/translations';
import { cueCheck, cueArrowRight, cueClose, IconComponent, cueStarFill, cueStar } from '@cue/ui/icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ImageWidthPipe, LocalizedStringPipe } from '@cue/utilities';
import { ControllableObjectsComponent, WorkingHoursComponent } from '@cue/ui';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { FileGalleryComponent } from '@cue/ui/file-gallery';
import { ExpansionPanelComponent, ExpansionPanelTitleDirective } from '@progress/kendo-angular-layout';
import { ResourceTitlePipe } from '@assist/ui/pipes';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Store } from '@ngrx/store';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { NavigationDetailInfo, NavigationService, QrIdListPipe } from '@cue/navigation-services';
import { forkJoin } from 'rxjs';
import { PoiComponent } from '@cue/poi';
import { map, tap } from 'rxjs/operators';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { OpenInNavigationButtonComponent, PoisCanBeShownInResourcePipe } from '@cue/navigation-widgets';

@Component({
  selector: 'assist-reservation-detail-resource-tab',
  templateUrl: './reservation-detail-resource-tab.component.html',
  styleUrls: ['./reservation-detail-resource-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    IconComponent,
    NewResourceMapComponent,
    ImageWidthPipe,
    WorkingHoursComponent,
    CustomUnsafeSanitizePipe,
    LocalizedStringPipe,
    FileGalleryComponent,
    OpenInNavigationButtonComponent,
    ExpansionPanelComponent,
    ResourceTitlePipe,
    NgTemplateOutlet,
    PoisCanBeShownInResourcePipe,
    ExpansionPanelTitleDirective,
    PoiComponent,
    ControllableObjectsComponent,
    QrIdListPipe,
    NgStyle,
  ],
})
export class ReservationDetailResourceTabComponent {
  resourceAttendees = input<ResourceAttendee[]>();
  eventStart = input<Date>();
  store = inject(Store);
  areas = injectAreas();
  navigationService = inject(NavigationService);
  resourceService = inject(ResourceService);
  configService = inject(ConfigService);
  translocoService = inject(TranslocoService);
  translation = translation;
  ICONS = {
    cueCheck,
    cueClose,
    cueStarFill,
    cueStar,
    cueArrowRight,
  };
  apiURL = this.configService.value.apiURL;

  favourites = toSignal(this.store.select(favourites));

  qrDetails: Record<string, WritableSignal<NavigationDetailInfo | null>> = {};
  resourceIds = computed(() => {
    return this.resourceAttendees().map((x) => x.resource.idForFiltering);
  });

  injector = inject(Injector);

  fetchQrDetail(id: string) {
    if (!this.qrDetails[id]) {
      runInInjectionContext(this.injector, () => {
        this.qrDetails[id] = signal(null);
      });

      this.navigationService
        .getNavigationDetail({
          qrCodeId: id,
          kioskId: null,
          appId: null,
        })
        .pipe(
          map((r) => r ?? null),
          tap((r) => this.qrDetails[id].set(r))
        )
        .subscribe();
    }
  }

  resourcesDetails = rxResource({
    params: () => this.resourceIds(),
    stream: ({ params }) => {
      const requests = params.map((id) =>
        this.navigationService
          .getNavigationDetail({ resourceId: id, kioskId: null, appId: null })
          .pipe(map((r) => r ?? null))
      );
      return forkJoin(requests);
    },
  });

  loadQrCodeDetails = explicitEffect([this.resourcesDetails.value], ([resourcesDetails]) => {
    if (resourcesDetails) {
      resourcesDetails.forEach((r) => {
        r.qrCodes.forEach((qr) => {
          if (qr.qrCodeId) {
            this.fetchQrDetail(qr.qrCodeId);
          }
        });
      });
    }
  });

  data = computed(() => {
    return this.resourceAttendees().map((rA) => {
      return {
        ...rA,
        breadcrumb: this.resourceService.getBreadcrumbForResourceSync(rA.resource),
        mapFile: this.resourceService.getNewMapUrlForResourceSync(rA.resource),
        isFavourite: this.favourites().schedules.some(
          (f) => f.resourceId.toUpperCase() === rA.resource.id.toUpperCase()
        ),
        serverDetail: this.resourcesDetails.value()?.find((r) => r?.resourceId === rA.resource.idForFiltering),
      };
    });
  });

  toggleFavourite(id: string, isFavourite: boolean) {
    this.store.dispatch(isFavourite ? removeFromFavourites({ id: id }) : addToFavourite({ id: id }));
  }
}
